import { z } from 'zod';
import { colourContrast, hexColourSchema, readableOn, shade } from './colour.js';

/**
 * Theme colours a provider may set. Each is `#rrggbb` or null for Hearth's own value. The
 * names mirror the CSS custom properties in apps/panel-web/src/index.css so
 * `themeCssVariables()` can apply a palette without any component knowing about branding.
 */
export const PALETTE_KEYS = [
  'primary',
  'secondary',
  'background',
  'sidebar',
  'card',
  'elevated',
  'border',
  'foreground',
  'mutedForeground',
  'success',
  'warning',
  'destructive',
  'info',
] as const;
export type PaletteKey = (typeof PALETTE_KEYS)[number];

export const PALETTE_LABELS: Record<PaletteKey, string> = {
  primary: 'Primary accent',
  secondary: 'Secondary accent',
  background: 'Main background',
  sidebar: 'Sidebar background',
  card: 'Surface / card',
  elevated: 'Elevated surface',
  border: 'Border',
  foreground: 'Primary text',
  mutedForeground: 'Secondary text',
  success: 'Success',
  warning: 'Warning',
  destructive: 'Error',
  info: 'Information',
};

export const paletteSchema = z.object(
  Object.fromEntries(PALETTE_KEYS.map((k) => [k, hexColourSchema.nullable().default(null)])) as {
    [K in PaletteKey]: z.ZodDefault<z.ZodNullable<typeof hexColourSchema>>;
  },
);
export type Palette = z.infer<typeof paletteSchema>;

export const EMPTY_PALETTE: Palette = Object.fromEntries(
  PALETTE_KEYS.map((k) => [k, null]),
) as Palette;

/** Hearth's own values (index.css), for previews, the "reset" action and contrast checks. */
export const HEARTH_DEFAULTS: Record<'light' | 'dark', Record<PaletteKey, string>> = {
  light: {
    primary: '#0079ad',
    secondary: '#f0f3f7',
    background: '#f5f7fa',
    sidebar: '#ffffff',
    card: '#ffffff',
    elevated: '#f0f3f7',
    border: '#e2e8f0',
    foreground: '#17202b',
    mutedForeground: '#5a6a80',
    success: '#15803d',
    warning: '#b45309',
    destructive: '#c8283a',
    info: '#2563eb',
  },
  dark: {
    primary: '#009ddc',
    secondary: '#202b38',
    background: '#0d1117',
    sidebar: '#111821',
    card: '#171f2a',
    elevated: '#202b38',
    border: '#303d4b',
    foreground: '#f5f7fa',
    mutedForeground: '#9aa5b4',
    success: '#3fcf85',
    warning: '#f5a524',
    destructive: '#f26571',
    info: '#5b9df9',
  },
};

/** Fonts a provider may pick: bundled variable fonts and system stacks. Never a URL. */
export const FONT_OPTIONS = [
  {
    id: 'inter',
    label: 'Inter',
    stack: "'Inter Variable', ui-sans-serif, system-ui, sans-serif",
    kind: 'sans',
  },
  {
    id: 'manrope',
    label: 'Manrope',
    stack: "'Manrope Variable', ui-sans-serif, system-ui, sans-serif",
    kind: 'sans',
  },
  {
    id: 'outfit',
    label: 'Outfit',
    stack: "'Outfit Variable', ui-sans-serif, system-ui, sans-serif",
    kind: 'sans',
  },
  {
    id: 'system',
    label: 'System UI',
    stack: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    kind: 'sans',
  },
  {
    id: 'source-serif',
    label: 'Source Serif',
    stack: "'Source Serif 4 Variable', Georgia, 'Times New Roman', serif",
    kind: 'serif',
  },
  { id: 'georgia', label: 'Georgia', stack: "Georgia, 'Times New Roman', serif", kind: 'serif' },
  {
    id: 'jetbrains-mono',
    label: 'JetBrains Mono',
    stack: "'JetBrains Mono Variable', ui-monospace, SFMono-Regular, Menlo, monospace",
    kind: 'mono',
  },
  {
    id: 'system-mono',
    label: 'System monospace',
    stack: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    kind: 'mono',
  },
] as const;
export type FontId = (typeof FONT_OPTIONS)[number]['id'];
export const fontIdSchema = z.enum(FONT_OPTIONS.map((f) => f.id) as [FontId, ...FontId[]]);

export const fontStack = (id: FontId): string => FONT_OPTIONS.find((f) => f.id === id)!.stack;

export const typographySchema = z.object({
  heading: fontIdSchema.default('inter'),
  body: fontIdSchema.default('inter'),
  mono: fontIdSchema.default('jetbrains-mono'),
});
export type Typography = z.infer<typeof typographySchema>;

export const RADIUS_OPTIONS = ['none', 'sm', 'md', 'lg', 'xl'] as const;
export const RADIUS_REM: Record<(typeof RADIUS_OPTIONS)[number], string> = {
  none: '0rem',
  sm: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
};

export const appearanceSchema = z.object({
  radius: z.enum(RADIUS_OPTIONS).default('md'),
  buttonStyle: z.enum(['default', 'pill', 'square']).default('default'),
  cardStyle: z.enum(['default', 'flat', 'elevated']).default('default'),
  sidebarDensity: z.enum(['comfortable', 'compact']).default('comfortable'),
  density: z.enum(['comfortable', 'compact']).default('comfortable'),
  navStyle: z.enum(['default', 'tinted', 'bold']).default('default'),
  /** What a visitor gets before they choose. */
  defaultTheme: z.enum(['dark', 'light', 'system']).default('dark'),
  /** Whether customers may switch between dark and light. */
  allowThemeSwitch: z.boolean().default(true),
});
export type Appearance = z.infer<typeof appearanceSchema>;

export const themeSchema = z.object({
  light: paletteSchema.default(EMPTY_PALETTE),
  dark: paletteSchema.default(EMPTY_PALETTE),
  typography: typographySchema.default({ heading: 'inter', body: 'inter', mono: 'jetbrains-mono' }),
  appearance: appearanceSchema.default({
    radius: 'md',
    buttonStyle: 'default',
    cardStyle: 'default',
    sidebarDensity: 'comfortable',
    density: 'comfortable',
    navStyle: 'default',
    defaultTheme: 'dark',
    allowThemeSwitch: true,
  }),
});
export type ThemeCustomisation = z.infer<typeof themeSchema>;

export const DEFAULT_THEME: ThemeCustomisation = themeSchema.parse({});

/** The palette with Hearth's defaults filled in where the provider left null. */
export function effectivePalette(
  palette: Palette,
  scheme: 'light' | 'dark',
): Record<PaletteKey, string> {
  const out = { ...HEARTH_DEFAULTS[scheme] };
  for (const k of PALETTE_KEYS) {
    const v = palette[k];
    if (v) out[k] = v;
  }
  return out;
}

/**
 * CSS custom properties for one scheme, only for keys the provider set, plus the derived
 * tokens (`--primary-foreground`, `--brand`, `--ring`, …) those keys imply. Applied by the
 * web on `:root` (light) and `.dark` through a constructed stylesheet.
 */
export function themeCssVariables(
  palette: Palette,
  scheme: 'light' | 'dark',
): Record<string, string> {
  const vars: Record<string, string> = {};
  const set = (name: string, value: string | null | undefined) => {
    if (value) vars[name] = value;
  };
  set('--primary', palette.primary);
  if (palette.primary) {
    vars['--primary-foreground'] = readableOn(palette.primary);
    vars['--brand'] = palette.primary;
    vars['--ring'] = palette.primary;
    vars['--series-1'] = palette.primary;
  }
  set('--secondary', palette.secondary);
  if (palette.secondary) vars['--secondary-foreground'] = readableOn(palette.secondary);
  set('--background', palette.background);
  set('--sidebar', palette.sidebar);
  if (palette.sidebar)
    vars['--sidebar-foreground'] = palette.foreground ?? readableOn(palette.sidebar);
  set('--card', palette.card);
  if (palette.card) {
    vars['--card-foreground'] = palette.foreground ?? readableOn(palette.card);
    vars['--popover'] = palette.elevated ?? palette.card;
    vars['--popover-foreground'] = vars['--card-foreground']!;
  }
  set('--elevated', palette.elevated);
  if (palette.elevated) {
    vars['--muted'] = palette.elevated;
    vars['--accent'] = palette.elevated;
    vars['--accent-foreground'] = palette.foreground ?? readableOn(palette.elevated);
  }
  set('--border', palette.border);
  if (palette.border)
    vars['--input'] = scheme === 'light' ? shade(palette.border, -0.1) : palette.border;
  set('--foreground', palette.foreground);
  if (palette.foreground) {
    vars['--card-foreground'] ??= palette.foreground;
    vars['--popover-foreground'] ??= palette.foreground;
    vars['--sidebar-foreground'] ??= palette.foreground;
    vars['--accent-foreground'] ??= palette.foreground;
  }
  set('--muted-foreground', palette.mutedForeground);
  set('--success', palette.success);
  if (palette.success) vars['--success-foreground'] = readableOn(palette.success);
  set('--warning', palette.warning);
  set('--destructive', palette.destructive);
  if (palette.destructive) vars['--destructive-foreground'] = readableOn(palette.destructive);
  set('--info', palette.info);
  return vars;
}

export interface ContrastWarning {
  scheme: 'light' | 'dark';
  /** The palette keys in question. */
  pair: [PaletteKey, PaletteKey];
  ratio: number;
  /** The minimum this pair should reach. */
  minimum: number;
}

/** Text pairs that must stay readable; the editor warns under these ratios (WCAG AA). */
const TEXT_PAIRS: [PaletteKey, PaletteKey, number][] = [
  ['foreground', 'background', 4.5],
  ['foreground', 'card', 4.5],
  ['foreground', 'sidebar', 4.5],
  ['mutedForeground', 'background', 4.5],
  ['mutedForeground', 'card', 4.5],
  ['primary', 'background', 3],
  ['primary', 'card', 3],
  ['destructive', 'card', 3],
  ['success', 'card', 3],
  ['warning', 'card', 3],
  ['info', 'card', 3],
  ['border', 'card', 1.2],
];

/** Every text/background pair below its minimum, for both schemes. */
export function contrastWarnings(
  theme: Pick<ThemeCustomisation, 'light' | 'dark'>,
): ContrastWarning[] {
  const out: ContrastWarning[] = [];
  for (const scheme of ['light', 'dark'] as const) {
    const eff = effectivePalette(theme[scheme], scheme);
    for (const [a, b, minimum] of TEXT_PAIRS) {
      // Only warn about pairs the provider touched; Hearth's own pairs pass.
      if (!theme[scheme][a] && !theme[scheme][b]) continue;
      const ratio = colourContrast(eff[a], eff[b]);
      if (ratio !== null && ratio < minimum) out.push({ scheme, pair: [a, b], ratio, minimum });
    }
  }
  return out;
}

/**
 * The floor the API enforces: primary text on the main surfaces must reach 3:1, or the panel
 * becomes unreadable for every customer. Everything softer is a warning in the editor.
 */
export function unreadablePairs(
  theme: Pick<ThemeCustomisation, 'light' | 'dark'>,
): ContrastWarning[] {
  return contrastWarnings(theme).filter(
    (w) => (w.pair[0] === 'foreground' || w.pair[0] === 'mutedForeground') && w.ratio < 3,
  );
}

/** Ready-made palettes a provider can start from. */
export const PALETTE_PRESETS: {
  id: string;
  label: string;
  light: Partial<Palette>;
  dark: Partial<Palette>;
}[] = [
  { id: 'hearth', label: 'Hearth', light: {}, dark: {} },
  {
    id: 'ocean',
    label: 'Ocean',
    light: { primary: '#0b63ce', background: '#f3f7fc', sidebar: '#ffffff' },
    dark: {
      primary: '#4f9cf9',
      background: '#0b1220',
      sidebar: '#0f172a',
      card: '#141f33',
      elevated: '#1b2a44',
      border: '#273a57',
    },
  },
  {
    id: 'ember',
    label: 'Ember',
    light: { primary: '#c2410c', background: '#faf6f2', sidebar: '#ffffff' },
    dark: {
      primary: '#fb923c',
      background: '#140f0c',
      sidebar: '#1a1310',
      card: '#211915',
      elevated: '#2c221c',
      border: '#3c2f27',
    },
  },
  {
    id: 'forest',
    label: 'Forest',
    light: { primary: '#15803d', background: '#f3f8f4', sidebar: '#ffffff' },
    dark: {
      primary: '#4ade80',
      background: '#0b140e',
      sidebar: '#101a13',
      card: '#152119',
      elevated: '#1d2d22',
      border: '#2b3f31',
    },
  },
  {
    id: 'violet',
    label: 'Violet',
    light: { primary: '#6d28d9', background: '#f7f5fc', sidebar: '#ffffff' },
    dark: {
      primary: '#a78bfa',
      background: '#100c1c',
      sidebar: '#161026',
      card: '#1c152f',
      elevated: '#271e40',
      border: '#382d57',
    },
  },
  {
    id: 'mono',
    label: 'Monochrome',
    light: { primary: '#111827', background: '#f8f8f8', sidebar: '#ffffff' },
    dark: {
      primary: '#e5e7eb',
      background: '#0a0a0a',
      sidebar: '#111111',
      card: '#171717',
      elevated: '#222222',
      border: '#2e2e2e',
    },
  },
];
